import React, { memo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import { getLoadingText } from '@/utils/getLoadingText';

/**
 * Modern Loading Component
 * Reusable loading spinner component with modern animations and design
 * 
 * @param {Object} props
 * @param {string} props.size - Size of spinner: 'xs' | 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {boolean} props.fullPage - Whether to show full page loading (default: false)
 * @param {boolean} props.inline - Whether to show just the spinner without wrapper (default: false)
 * @param {string} props.text - Optional text to display below spinner (defaults to translated loading for fullPage)
 * @param {string} props.color - Color variant: 'primary' | 'white' | 'green' | 'gray' (default: 'primary')
 * @param {string} props.variant - Spinner variant: 'spinner' | 'dots' | 'pulse' | 'icon' (default: 'spinner')
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.containerClassName - Additional CSS classes for container
 */
const Loading = memo(({
  size = 'md',
  fullPage = false,
  inline = false,
  text,
  color = 'primary',
  variant = 'spinner',
  className = '',
  containerClassName = '',
  ...props
}) => {
  const { colors } = useTheme();
  const resolvedText =
    text === undefined
      ? (fullPage ? getLoadingText() : undefined)
      : text;

  // Size configurations
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  // Icon size configurations
  const iconSizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
  };

  // Border width based on size
  const borderWidth = {
    xs: 'border',
    sm: 'border-2',
    md: 'border-2',
    lg: 'border-[3px]',
    xl: 'border-4',
  };

  // Get spinner color
  const getSpinnerColor = () => {
    switch (color) {
      case 'white':
        return { 
          borderColor: 'rgba(255, 255, 255, 0.3)',
          borderTopColor: 'white',
        };
      case 'green':
        return { 
          borderColor: 'rgba(12, 136, 98, 0.2)',
          borderTopColor: '#0C8862',
        };
      case 'gray':
        return { 
          borderColor: 'rgba(156, 163, 175, 0.3)',
          borderTopColor: '#9ca3af',
        };
      case 'primary':
      default:
        return { 
          borderColor: `${colors.primary.main}20`,
          borderTopColor: colors.primary.main,
        };
    }
  };

  // Get text color
  const getTextColor = () => {
    switch (color) {
      case 'white':
        return 'text-white';
      case 'green':
        return 'text-green-600 dark:text-green-400';
      case 'gray':
        return 'text-gray-600 dark:text-gray-400';
      case 'primary':
      default:
        return 'text-gray-600 dark:text-gray-300';
    }
  };

  // Modern spinner with gradient effect
  const ModernSpinner = () => (
    <div className="relative">
      {/* Outer ring with gradient */}
      <div
        className={cn(
          'rounded-full animate-spin',
          sizeClasses[size],
          borderWidth[size],
          'border-transparent',
          className
        )}
        style={{
          ...getSpinnerColor(),
          background: `conic-gradient(from 0deg, transparent, ${color === 'primary' ? colors.primary.main : color === 'green' ? '#0C8862' : color === 'white' ? 'white' : '#9ca3af'}, transparent)`,
          WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px))',
        }}
        {...props}
      />
      {/* Inner glow effect for larger sizes */}
      {(size === 'lg' || size === 'xl') && (
        <div
          className={cn(
            'absolute inset-0 rounded-full animate-pulse opacity-30',
            sizeClasses[size]
          )}
          style={{
            background: `radial-gradient(circle, ${color === 'primary' ? colors.primary.main : color === 'green' ? '#0C8862' : 'white'}40, transparent)`,
          }}
        />
      )}
    </div>
  );

  // Classic spinner (backward compatible)
  const ClassicSpinner = () => (
    <div
      className={cn(
        'rounded-full animate-spin',
        sizeClasses[size],
        borderWidth[size],
        'border-transparent',
        className
      )}
      style={getSpinnerColor()}
      {...props}
    />
  );

  // Dots loader
  const DotsLoader = () => (
    <div className={cn('flex items-center gap-1.5', className)} {...props}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'rounded-full animate-pulse',
            size === 'xs' ? 'h-1.5 w-1.5' :
            size === 'sm' ? 'h-2 w-2' :
            size === 'md' ? 'h-2.5 w-2.5' :
            size === 'lg' ? 'h-3 w-3' : 'h-4 w-4'
          )}
          style={{
            backgroundColor: color === 'primary' ? colors.primary.main : 
                           color === 'green' ? '#0C8862' : 
                           color === 'white' ? 'white' : '#9ca3af',
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1.4s',
          }}
        />
      ))}
    </div>
  );

  // Pulse loader
  const PulseLoader = () => (
    <div
      className={cn(
        'rounded-full animate-pulse',
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: color === 'primary' ? colors.primary.main : 
                         color === 'green' ? '#0C8862' : 
                         color === 'white' ? 'white' : '#9ca3af',
        opacity: 0.6,
      }}
      {...props}
    />
  );

  // Icon loader (using Loader2 from lucide-react)
  const IconLoader = () => (
    <Loader2
      className={cn(
        'animate-spin',
        iconSizeClasses[size],
        className
      )}
      style={{
        color: color === 'primary' ? colors.primary.main : 
               color === 'green' ? '#0C8862' : 
               color === 'white' ? 'white' : '#9ca3af',
      }}
      {...props}
    />
  );

  // Get the appropriate loader based on variant
  const getLoader = () => {
    switch (variant) {
      case 'dots':
        return <DotsLoader />;
      case 'pulse':
        return <PulseLoader />;
      case 'icon':
        return <IconLoader />;
      case 'spinner':
      default:
        return <ModernSpinner />;
    }
  };

  const loader = getLoader();

  // If inline, return just the loader (for buttons, etc.)
  if (inline) {
    return loader;
  }

  // If full page, return full page layout
  if (fullPage) {
    return (
      <div className={cn(
        'min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900',
        containerClassName
      )}>
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            {loader}
          </div>
          {resolvedText && (
            <p className={cn('mt-4 text-base font-medium animate-pulse', getTextColor())}>
              {resolvedText}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default inline loading
  return (
    <div className={cn(
      'flex items-center justify-center',
      containerClassName
    )}>
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          {loader}
        </div>
        {resolvedText && (
          <p className={cn('text-sm font-medium', getTextColor())}>
            {resolvedText}
          </p>
        )}
      </div>
    </div>
  );
});

Loading.displayName = 'Loading';

export default Loading;
export { Loading };
